import { useState } from 'react';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  getListOrganizationDomainsQueryKey,
  getListOrganizationDomainsQueryOptions,
  useDeleteOrganizationDomain,
  useDisableOrganizationDomain,
  useVerifyOrganizationDomain,
  type DomainPairDiagnosticDto,
  type DomainPairDto,
} from '@sara/api-client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@sara/ui/components/alert-dialog';
import { buttonVariants } from '@sara/ui/components/button';
import { toast } from '@sara/ui/components/sonner';
import { cn } from '@sara/ui/lib/utils';
import { DomainList } from '../../components/DomainList';
import { toSuspense } from '../../../../lib/query';
import type { DomainListContainerProps } from './types';

export function DomainListContainerContent({ orgId, onCreate }: DomainListContainerProps) {
  const queryClient = useQueryClient();
  const [disabling, setDisabling] = useState<DomainPairDto | null>(null);
  const [deleting, setDeleting] = useState<DomainPairDto | null>(null);
  const [verifyPending, setVerifyPending] = useState<ReadonlySet<string>>(new Set());
  const [diagnostics, setDiagnostics] = useState<
    Record<string, DomainPairDiagnosticDto[] | undefined>
  >({});

  const { data: domains } = useSuspenseQuery(
    toSuspense(getListOrganizationDomainsQueryOptions({ orgId }))
  );

  const { mutate: verify } = useVerifyOrganizationDomain({
    mutation: {
      onSuccess: (data, variables) => {
        setDiagnostics((prev) => ({ ...prev, [variables.id]: data.diagnostics }));
        if (data.status === 'ACTIVE') {
          toast.success('Domain verified — invite links will use the custom hostname.');
        } else {
          toast.error('Verification incomplete — see details below.');
        }
        void queryClient.invalidateQueries({
          queryKey: getListOrganizationDomainsQueryKey({ orgId }),
        });
      },
      onError: (err, variables) => {
        setDiagnostics((prev) => {
          const next = { ...prev };
          delete next[variables.id];
          return next;
        });
        toast.error('Failed to verify domain', {
          description: err instanceof Error ? err.message : undefined,
        });
      },
      onSettled: (_data, _err, variables) => {
        setVerifyPending((prev) => {
          const next = new Set(prev);
          next.delete(variables.id);
          return next;
        });
      },
    },
  });

  const { mutate: disablePair, isPending: isDisabling } = useDisableOrganizationDomain({
    mutation: {
      onSuccess: (_data, variables) => {
        const target = domains.find((d) => d.id === variables.id);
        void queryClient.invalidateQueries({
          queryKey: getListOrganizationDomainsQueryKey({ orgId }),
        });
        setDisabling(null);
        toast.success(`Disabled ${target?.interview.hostname ?? 'domain'}`);
      },
      onError: (err) => {
        toast.error('Failed to disable domain', {
          description: err instanceof Error ? err.message : undefined,
        });
      },
    },
  });

  const { mutate: deletePair, isPending: isDeleting } = useDeleteOrganizationDomain({
    mutation: {
      onSuccess: (_data, variables) => {
        const target = domains.find((d) => d.id === variables.id);
        void queryClient.invalidateQueries({
          queryKey: getListOrganizationDomainsQueryKey({ orgId }),
        });
        setDeleting(null);
        toast.success(`Deleted ${target?.interview.hostname ?? 'domain'}`);
      },
      onError: (err) => {
        toast.error('Failed to delete domain', {
          description: err instanceof Error ? err.message : undefined,
        });
      },
    },
  });

  function handleVerify(pair: DomainPairDto) {
    setVerifyPending((prev) => {
      const next = new Set(prev);
      next.add(pair.id);
      return next;
    });
    verify({ id: pair.id, params: { orgId } });
  }

  return (
    <>
      <DomainList
        domains={domains}
        diagnosticsByPairId={diagnostics}
        pendingVerifyIds={verifyPending}
        onVerify={handleVerify}
        onDisable={setDisabling}
        onDelete={setDeleting}
        onCreate={onCreate}
      />

      <AlertDialog
        open={disabling !== null}
        onOpenChange={(open) => {
          if (!open && !isDisabling) setDisabling(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable custom domain?</AlertDialogTitle>
            <AlertDialogDescription>
              {disabling ? (
                <>
                  Invite links pointing at{' '}
                  <span className="font-medium text-foreground">
                    {disabling.interview.hostname}
                  </span>{' '}
                  will stop resolving. New invites for this organization will fall back to the
                  default hostname.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className={cn(buttonVariants({ variant: 'outline', shape: 'pill', size: 'sm' }))}
              disabled={isDisabling}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(buttonVariants({ variant: 'destructive', shape: 'pill', size: 'sm' }))}
              disabled={isDisabling}
              onClick={(event) => {
                event.preventDefault();
                if (!disabling) return;
                disablePair({ id: disabling.id, params: { orgId } });
              }}
            >
              {isDisabling ? 'Disabling…' : 'Disable domain'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setDeleting(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete custom domain?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting ? (
                <>
                  This permanently removes{' '}
                  <span className="font-medium text-foreground">{deleting.interview.hostname}</span>{' '}
                  and its paired API hostname from this organization. You&rsquo;ll need to
                  re-request it from scratch to use it again.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className={cn(buttonVariants({ variant: 'outline', shape: 'pill', size: 'sm' }))}
              disabled={isDeleting}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(buttonVariants({ variant: 'destructive', shape: 'pill', size: 'sm' }))}
              disabled={isDeleting}
              onClick={(event) => {
                event.preventDefault();
                if (!deleting) return;
                deletePair({ id: deleting.id, params: { orgId } });
              }}
            >
              {isDeleting ? 'Deleting…' : 'Delete domain'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
